import { format } from 'date-fns'
import { FileText, Loader2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Link } from '@/components/ui/Link'
import { type DriverMvrState, MvrStatus } from '../../models/mvr'
import { MvrOutcome } from './MvrOutcome'

function inFlightLabel(status: MvrStatus | null) {
  if (status === MvrStatus.PROCESSING) return 'Processing at Checkr'
  return status === MvrStatus.IN_PROGRESS ? 'In progress' : 'Requested'
}

/** A driver with no MVR renders nothing, so "never pulled" stays visibly
 * different from "failed". */
export function MvrStatusCell({ state }: { state: DriverMvrState | undefined }) {
  if (!state) return <span className="text-muted-foreground">—</span>
  if (state.unreportedAccidents === 0) return <LatestPull state={state} />

  // Flags the newest completed record, so it stays while a later pull runs or fails.
  return (
    <span className="flex flex-wrap items-center gap-2">
      <LatestPull state={state} />
      <Badge variant="warning">
        {state.unreportedAccidents} unreported {state.unreportedAccidents === 1 ? 'accident' : 'accidents'}
      </Badge>
    </span>
  )
}

function LatestPull({ state }: { state: DriverMvrState }) {
  if (state.inFlight) {
    return (
      <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <Loader2 className="size-3.5 shrink-0 animate-spin" />
        {inFlightLabel(state.latestStatus)}
      </span>
    )
  }

  if (state.latestStatus === MvrStatus.FAILED || state.latestStatus === MvrStatus.NO_RECORD) {
    return <MvrOutcome state={state} />
  }

  if (!state.lastPulledAt) return <span className="text-muted-foreground">—</span>

  const pulled = format(new Date(state.lastPulledAt), 'MMM d, yyyy')
  return (
    <span className="flex items-center gap-2 text-xs">
      {state.latestDocumentUrl ? (
        // The row itself opens the driver dialog, so the link must not bubble.
        <Link
          kind="util"
          size="sm"
          icon={<FileText className="size-3.5 shrink-0" />}
          href={state.latestDocumentUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {pulled}
        </Link>
      ) : (
        pulled
      )}
      {!state.isCurrent && <Badge variant="outline">Out of date</Badge>}
    </span>
  )
}
